import axiosInstance from 'api/axiosInstance';
import type { AxiosRequestConfig } from 'axios';
import type {
  AIAgentEnum,
  ConversationHistory,
  ConversationMetadata,
  ConversationMetadataType,
  ConversationQuery,
  SuggestedQuestion,
  SuggestedQuestionUseCaseEnum,
} from 'types/ai.types';
import type { Paging } from 'types/pagination.types';

export const getConversationUsage = (): Promise<{
  monthly_message_count: number;
  monthly_message_limit: number;
  conversation_message_limit: number;
}> => axiosInstance.get('/v2/conversations/messages/monthly-usage');

export const createConversation = (
  body: ConversationQuery,
  config: AxiosRequestConfig,
): Promise<ConversationHistory> =>
  axiosInstance
    .post('/v2/conversations/stream', body, config)
    .then(({ data }) => ({ ...data, uid: new Date() }));

export const getConversations = (params: {
  page: number;
  perPage: number;
  source?: ConversationMetadataType;
  ai_agent?: AIAgentEnum;
}): Promise<{
  data: ConversationHistory[];
  paging: Paging;
}> => {
  return axiosInstance.get('/v2/conversations', { params });
};

export const updateConversation = (
  { id, ...body }: ConversationQuery,
  config: AxiosRequestConfig,
): Promise<ConversationHistory> =>
  axiosInstance
    .post(`/v2/conversations/${id}/messages/stream`, body, config)
    .then(({ data }) => ({ ...data, uid: new Date() }));

export const getConversationMessages = (
  id?: string,
): Promise<ConversationHistory> =>
  axiosInstance
    .get(`/v2/conversations/${id}`)
    .then(({ data }) => ({ ...data, uid: new Date() }));

export const patchConversation = (
  conversationId: string,
  body: Pick<ConversationHistory, 'rating' | 'intercomShareConversation'>,
): Promise<ConversationHistory> =>
  axiosInstance
    .patch(`/v2/conversations/${conversationId}`, body)
    .then(({ data }) => data);

export const talkToCoach = (
  conversationId: string,
  messageId: string,
): Promise<any> => {
  return axiosInstance.put(
    `/v2/conversations/${conversationId}/messages/${messageId}/intercom-cta`,
  );
};

export const submitDataAccessForm = (
  conversationId: string,
  messageId: string,
  body: any,
  config: AxiosRequestConfig,
): Promise<any> => {
  return axiosInstance.post(
    `/v2/conversations/${conversationId}/messages/${messageId}/form-submission`,
    body,
    config,
  );
};

export const getConversationMetadata = (
  conversationId: string,
): Promise<{ data: ConversationMetadata[] }> =>
  axiosInstance.get(`/v2/conversations/${conversationId}/metadata`);

export const postConversationMetadata = (
  metadata: ConversationMetadata,
): Promise<ConversationMetadata> =>
  axiosInstance.post(`/v2/conversations/metadata`, metadata);

export const searchExpenseCategories = (
  text: string,
  lang = 'en',
): Promise<any> =>
  axiosInstance.get('/v2/expenses/categories/search', {
    params: { text, lang },
  });

export const predictExpenseCategory = (text: string): Promise<any> =>
  axiosInstance.get('/v2/expenses/categories/predict', { params: { text } });

export const getSuggestedQuestions = (
  use_case: SuggestedQuestionUseCaseEnum,
): Promise<{ data: SuggestedQuestion[] }> =>
  axiosInstance.get('/v2/conversations/suggested-prompts', {
    params: { use_case },
  });

export const postSuggestedQuestions = (
  promptId: string,
  order: number,
  prompt_metadata?: Record<string, any>,
): Promise<any> =>
  axiosInstance.post('/v2/conversations/suggested-prompts', {
    promptId,
    order,
    prompt_metadata,
  });
